'use client';

import { useEffect, useState } from 'react';

import { ContactMessage } from '@j-s/dr-shared';

import {
  addTicketReply,
  closeTicketByClient,
  markTicketReadByClient,
  reopenTicketByClient,
  subscribeTicket,
} from '../services/contactMessageService';

export function useTicketTracker(ticketId: string | null, isOpen: boolean) {
  const [ticket, setTicket] = useState<ContactMessage | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ticketId || !isOpen) {
      return;
    }

    const unsubscribe = subscribeTicket(ticketId, setTicket);

    return () => {
      unsubscribe();
      setTicket(null);
    };
  }, [ticketId, isOpen]);

  const currentTicketId = ticket?.id;
  const currentTicketStatus = ticket?.status;

  useEffect(() => {
    if (currentTicketId && isOpen && currentTicketStatus === 'replied') {
      markTicketReadByClient(currentTicketId).catch((err) => {
        console.error('Failed to mark ticket as read by client:', err);
      });
    }
  }, [currentTicketId, currentTicketStatus, isOpen]);

  const handleSendReply = async (text: string) => {
    if (!ticket) return;

    await addTicketReply(
      ticket.id,
      {
        author: 'client',
        authorName: ticket.name,
        message: text,
      },
      'unread'
    );
  };

  const handleCloseTicket = async () => {
    if (!ticket) return;
    try {
      await closeTicketByClient(ticket.id);
    } catch (err) {
      console.error('Failed to close ticket:', err);
    }
  };

  const handleReopenTicket = async () => {
    if (!ticket) return;
    try {
      await reopenTicketByClient(ticket.id);
    } catch (err) {
      console.error('Failed to reopen ticket:', err);
    }
  };

  const handleCopyLink = () => {
    if (!ticket) return;

    const url = `${window.location.origin}/#napisz-do-nas?ticket=${ticket.id}&token=${ticket.accessToken}`;

    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return {
    ticket,
    copied,
    handleSendReply,
    handleCloseTicket,
    handleReopenTicket,
    handleCopyLink,
  };
}
